import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { WorkflowApiService } from '../../features/workflow/workflow-api.service';
import { CurrentContext } from '../context/current-context.model';
import { CurrentContextService } from '../context/current-context.service';
import { LocalizationService } from '../localization/localization.service';
import { NavigationTreeService } from '../navigation/navigation-tree.service';
import { WAYFINDING_JOBS, WAYFINDING_PLAYS } from './wayfinding-catalog';
import { CRAWLED_WAYFINDING_PLAYS } from './wayfinding-crawled-plays';
import { composeJobPath, matchPublishedWorkflows, normalize, pickCannedPlay } from './wayfinding-compose';
import { buildLiveWayfindingJobs } from './wayfinding-index';
import {
  contextBoost,
  continuePlayFrom,
  describeHere,
  isWorkScreen,
  normalizeRoute,
  playForLocation,
  shouldContinuePlay
} from './wayfinding-navigator';
import {
  WayfindingJob,
  WayfindingPlay,
  WayfindingKnowledgeHit,
  WayfindingSearchContext,
  WayfindingSearchResult
} from './wayfinding.models';

const SEQUENCE_HINTS = ['بعد', 'سپس', 'مسیر', 'فرآیند', 'فرایند', 'گردش', 'workflow', 'then', 'after', 'steps'];

@Injectable({ providedIn: 'root' })
export class WayfindingService {
  private readonly http = inject(HttpClient);
  private readonly localization = inject(LocalizationService);
  private readonly navigationTree = inject(NavigationTreeService);
  private readonly workflowApi = inject(WorkflowApiService);
  private readonly currentContext = inject(CurrentContextService);
  private corpusSynced = false;
  private corpusSyncSkipped = false;
  private corpusSyncRequest$: Observable<boolean> | null = null;
  private definitionsLoaded = false;
  private definitionsFailed = false;
  private definitionsRequest$: Observable<boolean> | null = null;
  private definitionsGeneration = 0;
  private readonly definitions = signal<Array<{ id: string; name: string; description?: string; status: string; isActive: boolean }>>([]);

  publishedDefinitionName(id: string): string {
    if (!id) {
      return '';
    }

    return this.definitions().find(item => item.id === id)?.name ?? '';
  }

  search(rawQuery: string, context: WayfindingSearchContext = {}): WayfindingSearchResult {
    const query = normalize(rawQuery);
    const tokens = query.split(' ').filter(Boolean);
    const wantsPath = tokens.length >= 3 || SEQUENCE_HINTS.some(hint => query.includes(hint));
    const currentRoute = normalizeRoute(context.currentRoute ?? '');
    const recents = (context.recents ?? []).filter(item => isWorkScreen(item.route));
    const allPlays = [...WAYFINDING_PLAYS, ...CRAWLED_WAYFINDING_PLAYS];
    const herePlay = playForLocation(currentRoute, allPlays);
    const here = describeHere(
      currentRoute,
      this.navigationTree.visibleFlatItems().map(item => ({ route: item.route, label: item.label })),
      key => this.t(key),
      herePlay
    );
    const liveJobs = buildLiveWayfindingJobs(
      this.navigationTree.visibleFlatItems(),
      WAYFINDING_JOBS,
      key => this.t(key)
    );
    const composedJobs = composeJobPath(query, liveJobs);

    if (shouldContinuePlay(rawQuery, currentRoute, herePlay, composedJobs.map(job => job.route), key => this.t(key)) && herePlay) {
      const continued = continuePlayFrom(herePlay, currentRoute);
      return this.withWorkflows({
        query: rawQuery.trim(),
        here,
        recents,
        continued: true,
        workflowPreferred: false,
        workflows: [],
        plays: [{ ...continued, score: 100 }],
        jobs: [],
        screens: [],
        knowledge: []
      }, rawQuery);
    }

    const cannedPlays = allPlays
      .map(play => ({
        ...play,
        score: scoreEntry(query, tokens, play.synonyms, this.t(play.titleKey), this.t(play.descriptionKey))
          + contextBoost(play.steps[0]?.route ?? '', currentRoute, recents)
          + (herePlay && play.id === herePlay.id ? 30 : 0)
      }))
      .map(play => ({ ...play, score: wantsPath ? play.score + 12 : play.score }))
      .filter(play => play.score > 0)
      .sort((a, b) => b.score - a.score);

    const jobs = WAYFINDING_JOBS
      .map(job => ({
        ...job,
        score: scoreEntry(query, tokens, job.synonyms, this.t(job.titleKey), this.t(job.descriptionKey))
          + contextBoost(job.route, currentRoute, recents)
      }))
      .filter(job => job.score > 0)
      .sort((a, b) => b.score - a.score);

    const screens = query
      ? this.navigationTree.visibleFlatItems()
        .map(item => {
          const label = this.t(item.label);
          return {
            route: item.route,
            label,
            score: scoreEntry(query, tokens, [label, item.key, item.route], label, '')
              + contextBoost(item.route, currentRoute, recents)
          };
        })
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 6)
      : [];

    if (composedJobs.length >= 1) {
      const canned = pickCannedPlay(composedJobs, allPlays);
      const primary: WayfindingPlay & { score: number } = canned
        ? { ...canned, score: 100 }
        : {
          id: 'composed-path',
          titleKey: 'wayfinding.composed.title',
          descriptionKey: 'wayfinding.composed.description',
          synonyms: [],
          steps: composedJobs.map(job => ({
            route: job.route,
            titleKey: job.titleKey,
            whyKey: job.descriptionKey
          })),
          intentKey: 'workflow.process.start',
          score: 100
        };
      return this.withWorkflows({
        query: rawQuery.trim(),
        here,
        recents,
        continued: false,
        workflowPreferred: false,
        workflows: [],
        plays: [primary],
        jobs: [],
        screens: [],
        knowledge: []
      }, rawQuery);
    }

    return this.withWorkflows({
      query: rawQuery.trim(),
      here,
      recents,
      continued: false,
      workflowPreferred: false,
      workflows: [],
      plays: cannedPlays.slice(0, 4),
      jobs: jobs.slice(0, 6),
      screens,
      knowledge: []
    }, rawQuery);
  }

  suggestedPlays(): WayfindingPlay[] {
    return WAYFINDING_PLAYS;
  }

  suggestedJobs(): WayfindingJob[] {
    return WAYFINDING_JOBS;
  }

  ensureDefinitionsLoaded(force = false): Observable<boolean> {
    if (this.definitionsLoaded && !force) {
      return of(true);
    }

    if (this.definitionsFailed && !force) {
      return of(false);
    }

    if (this.definitionsRequest$ && !force) {
      return this.definitionsRequest$;
    }

    const generation = ++this.definitionsGeneration;
    this.definitionsFailed = false;
    this.definitionsRequest$ = this.resolveContext().pipe(
      switchMap(context => {
        if (!hasPermissionAction(context, 'workflow.definitions', 'view')) {
          if (generation === this.definitionsGeneration) {
            this.definitionsFailed = true;
          }

          return of(false);
        }

        return this.workflowApi.listDefinitions({ silent: true }).pipe(
          tap(items => {
            if (generation !== this.definitionsGeneration) {
              return;
            }

            this.definitions.set(items ?? []);
            this.definitionsLoaded = true;
            this.definitionsFailed = false;
          }),
          map(() => generation === this.definitionsGeneration && this.definitionsLoaded),
          catchError(() => {
            if (generation !== this.definitionsGeneration) {
              return of(this.definitionsLoaded);
            }

            this.definitionsLoaded = this.definitions().length > 0;
            this.definitionsFailed = !this.definitionsLoaded;
            return of(this.definitionsLoaded);
          })
        );
      }),
      finalize(() => {
        if (generation === this.definitionsGeneration) {
          this.definitionsRequest$ = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.definitionsRequest$;
  }

  ensureCorpusSynced(): Observable<boolean> {
    if (this.corpusSynced) {
      return of(true);
    }

    if (this.corpusSyncSkipped) {
      return of(false);
    }

    if (this.corpusSyncRequest$) {
      return this.corpusSyncRequest$;
    }

    this.corpusSyncRequest$ = this.resolveContext().pipe(
      switchMap(context => {
        if (!hasPermissionAction(context, 'knowledge.retrieve', 'view')) {
          this.corpusSyncSkipped = true;
          return of(false);
        }

        return this.http.post(`${environment.apiBaseUrl}/api/v1/knowledge/wayfinding-corpus/sync`, {}, {
          params: { silent: 'true' }
        }).pipe(
          tap(() => {
            this.corpusSynced = true;
          }),
          map(() => true),
          catchError(() => {
            this.corpusSyncSkipped = true;
            return of(false);
          })
        );
      }),
      finalize(() => {
        this.corpusSyncRequest$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.corpusSyncRequest$;
  }

  retrieveKnowledge(rawQuery: string): Observable<WayfindingKnowledgeHit[]> {
    const query = rawQuery.trim();
    if (query.length < 2) {
      return of([]);
    }

    return this.ensureCorpusSynced().pipe(
      switchMap(synced => {
        if (!synced) {
          return of([]);
        }

        return this.http.post<{ items: Array<{ text: string; score: number; citation?: { documentTitle?: string } }> }>(
          `${environment.apiBaseUrl}/api/v1/knowledge/retrieve`,
          { query, topK: 5, consumerModule: 'admin-wayfinding', filters: {} },
          { params: { silent: 'true' } }
        ).pipe(
          map(response => (response.items ?? []).map(item => ({
            text: item.text,
            title: item.citation?.documentTitle ?? '',
            score: Number(item.score ?? 0)
          }))),
          catchError(() => of([]))
        );
      })
    );
  }

  private withWorkflows(result: WayfindingSearchResult, rawQuery: string): WayfindingSearchResult {
    if (result.continued) {
      return { ...result, workflows: [], workflowPreferred: false };
    }

    const workflows = matchPublishedWorkflows(rawQuery, this.definitions());
    const workflowPreferred = (workflows[0]?.score ?? 0) >= 80;
    return { ...result, workflows, workflowPreferred };
  }

  private t(key: string): string {
    return this.localization.translate(key);
  }

  private resolveContext(): Observable<CurrentContext | null> {
    const loaded = this.currentContext.context();
    if (loaded) {
      return of(loaded);
    }

    return this.currentContext.loadContext().pipe(catchError(() => of(null)));
  }
}

function hasPermissionAction(context: CurrentContext | null, permissionKey: string, action: string): boolean {
  if (!context) {
    return false;
  }

  const roles = new Set(context.roles ?? []);
  if (roles.has('SystemAdmin') || context.isPlatformAdmin) {
    return true;
  }

  const node = (context.permissions ?? []).find(item => item.key === permissionKey);
  if (!node) {
    return false;
  }

  return (node.actions ?? []).some(item => item.toLowerCase() === action.toLowerCase());
}

export { normalize } from './wayfinding-compose';

function scoreEntry(query: string, tokens: string[], synonyms: string[], title: string, description: string): number {
  if (!query) {
    return 0;
  }

  const haystack = normalize([title, description, ...synonyms].join(' '));
  if (!haystack) {
    return 0;
  }

  if (haystack.includes(query)) {
    return 100;
  }

  const matched = tokens.filter(token => token.length > 1 && haystack.includes(token)).length;
  if (matched === 0) {
    return 0;
  }

  return matched === tokens.length ? 80 : matched * 18;
}
